import { Component, CompositionState, makeError, VElement, ViewMount, merge, Adapter } from '@textbus/core'
import {
  ComponentSetup,
  createDynamicRef,
  DynamicRef,
  getCurrentInstance, Injector,
  jsx,
  JSXNode, onUnmounted,
  onUpdated, ReflectiveInjector,
  ViewFlyNode,
} from '@viewfly/core'
import { DomAdapter } from '@textbus/platform-browser'

const adapterError = makeError('ViewflyDOMRenderer')

export interface ViewComponentProps<T extends Component> {
  component: T
  rootRef: DynamicRef<Element>
}

export interface ViewflyAdapterComponents {
  [key: string]: ComponentSetup<ViewComponentProps<any>>
}

export class ViewflyAdapter extends DomAdapter<ViewFlyNode, ViewFlyNode> {
  private components: ViewflyAdapterComponents = {}

  private componentRefs = new WeakMap<Component, DynamicRef<Element>>()

  constructor(components: ViewflyAdapterComponents,
              mount: ViewMount<ViewFlyNode, Element>
  ) {
    super({
      createCompositionNode(compositionState: CompositionState,
                            updateNativeCompositionNode: (nativeNode: (Element | null)) => void) {
        const ref = createDynamicRef<Element>(node => {
          updateNativeCompositionNode(node)
          return () => {
            updateNativeCompositionNode(null)
          }
        })
        return jsx('span', {
          style: {
            textDecoration: 'underline'
          },
          ref,
          children: [compositionState.text]
        }, compositionState.slot.id)
      },
      getParentNode(node: Element | Text): Element | null {
        return (node as Node).parentNode as Element
      },
      getChildNodes(parentElement: Element): Array<Element | Text> {
        return Array.from(parentElement.childNodes) as Element[]
      },
      isNativeElementNode(node: Element | Text): node is Element {
        return node instanceof Element
      },
      getChildByIndex(parentElement, index) {
        return parentElement.childNodes[index] as Element
      },
      getAndUpdateSlotRootNativeElement(vEle: VElement, update: (nativeElement: (Element | null)) => void) {
        const currentRef = vEle.attrs.get('ref')
        const ref = createDynamicRef<Element>(nativeNode => {
          update(nativeNode)
          return () => {
            update(null)
          }
        })
        if (Array.isArray(currentRef)) {
          currentRef.push(ref)
        } else if (typeof currentRef === 'function' || typeof currentRef === 'object') {
          // 两个判断用于兼容 viewfly 2/3
          vEle.attrs.set('ref', [currentRef, ref])
        } else {
          vEle.attrs.set('ref', ref)
        }
      },
      componentRender: (component: Component<any>): ViewFlyNode => {
        const comp = this.components[component.name] || this.components['*']
        if (comp) {
          let ref = this.componentRefs.get(component)
          if (!ref) {
            ref = createDynamicRef<Element>(rootNode => {
              this.componentRootElementCaches.set(component, rootNode)
              return () => {
                // 当组件移动层级位置到原位置之前并重新渲染后，由于时序的原因，再删除缓存会导致组件找不到对应视图节点
                if (this.componentRootElementCaches.get(component) === rootNode) {
                  this.componentRootElementCaches.remove(component)
                }
              }
            })
            this.componentRefs.set(component, ref)
          }
          return jsx(comp, {
            component,
            rootRef: ref
          }, component.id)
        }
        throw adapterError(`cannot found view component \`${component.name}\`!`)
      },
      vElementToViewElement(vNode: VElement, children: Array<JSXNode>): ViewFlyNode {
        let key: any
        const props = Array.from(vNode.attrs).reduce((a, b) => {
          const propName = b[0]
          if (propName === 'key') {
            key = b[1]
            return a
          }
          a[propName] = b[1]
          return a
        }, {} as Record<string, any>)
        if (vNode.classes.size) {
          props.class = Array.from(vNode.classes).join(' ')
        }
        if (vNode.styles) {
          props.style = Array.from(vNode.styles).reduce((a, b) => {
            a[b[0]] = b[1]
            return a
          }, {} as Record<string, any>)
        }
        if (children.length) {
          props.children = children
        }
        return jsx(vNode.tagName, props, key)
      }
    }, mount)

    let isRoot = true
    Object.entries(components).forEach(([key, viewFlyComponent]) => {
      this.components[key] = (props: ViewComponentProps<Component>) => {
        const comp = getCurrentInstance()
        const textbusComponent = props.component
        const subscription = merge(textbusComponent.changeMarker.onChange,
          textbusComponent.changeMarker.onForceChange).subscribe(() => {
          if (textbusComponent.changeMarker.dirty) {
            comp.markAsDirtied()
          }
        })
        onUnmounted(() => {
          subscription.unsubscribe()
        })
        if (isRoot) {
          onUpdated(() => {
            this.onViewUpdated.next()
          })
          isRoot = false
        }
        onUpdated(() => {
          textbusComponent.changeMarker.rendered()
          if (!this.componentRootElementCaches.get(textbusComponent)) {
            // eslint-disable-next-line max-len
            throw adapterError(`Component \`${textbusComponent.name}\` is not bound to rootRef, you must bind rootRef to the root element node of the component view.`)
          }
        })
        return viewFlyComponent(props)
      }
    })
  }

  override render(rootComponent: Component, injector: Injector): void | (() => void) {
    const childInjector = new ReflectiveInjector(injector, [{
      provide: Adapter,
      useValue: this
    }, {
      provide: DomAdapter,
      useValue: this
    }, {
      provide: ViewflyAdapter,
      useValue: this
    }])
    return super.render(rootComponent, childInjector)
  }

  override copy() {
    document.execCommand('copy')
  }
}
